import React, { useState, useEffect } from "react";
import { Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Label from "@/components/ui/Label";
import { getTotalHours, showToastNotification } from "@/utility/helper";
import { ThrowError } from "@/api/callApi";
import CommonModal from "@/components/common/CommonModal";
import { ShiftService } from "@/services/Shift/Shift";
import type {
  IShiftCreatePayload,
  AddShiftModalProps,
} from "@/interface/shift.interface";

const initialForm: IShiftCreatePayload = {
  shiftName: "",
  startTime: "",
  endTime: "",
  status: "active",
};

const AddShiftModal: React.FC<AddShiftModalProps> = ({
  show,
  onClose,
  onSuccess,
  editData,
}) => {
  const [form, setForm] = useState<IShiftCreatePayload>(initialForm);
  const [errors, setErrors] = useState<
    Partial<Record<keyof IShiftCreatePayload, string>>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = Boolean(editData);

  useEffect(() => {
    if (show) {
      if (editData) {
        setForm({
          shiftName: editData.shiftName,
          startTime: editData.startTime,
          endTime: editData.endTime,
          status: editData.status,
        });
      } else {
        setForm(initialForm);
      }
      setErrors({});
    }
  }, [show, editData]);

  const handleChange = (field: keyof IShiftCreatePayload, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const newErrors: Partial<Record<keyof IShiftCreatePayload, string>> = {};
    if (!form.shiftName.trim()) newErrors.shiftName = "Shift name is required";
    if (!form.startTime) newErrors.startTime = "Start time is required";
    if (!form.endTime) newErrors.endTime = "End time is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = { ...form };
      let response;
      if (isEditMode && editData) {
        response = await ShiftService.updateShift(editData.id, payload);
      } else {
        response = await ShiftService.createShift(payload);
      }

      if (!response?.success) {
        showToastNotification(
          "danger",
          response?.message ||
            `Failed to ${isEditMode ? "update" : "create"} shift`,
        );
        return;
      }

      showToastNotification(
        "success",
        response?.message ||
          `Shift ${isEditMode ? "updated" : "created"} successfully`,
      );
      onSuccess?.();
      onClose();
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalHours = getTotalHours(form.startTime, form.endTime);

  return (
    <CommonModal
      title={isEditMode ? "Edit Shift" : "Add Shift"}
      show={show}
      onClose={onClose}
      className="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5 pt-2">
        <div className="space-y-1.5">
          <Label htmlFor="shiftName">
            Shift Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="shiftName"
            placeholder="e.g. Day Shift"
            value={form.shiftName}
            onChange={(e) => handleChange("shiftName", e.target.value)}
            className={
              errors.shiftName
                ? "border-red-500 focus-visible:ring-red-500"
                : ""
            }
          />
          {errors.shiftName && (
            <p className="text-xs text-red-500">{errors.shiftName}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="startTime">
              Start Time <span className="text-red-500">*</span>
            </Label>
            <Input
              id="startTime"
              type="time"
              value={form.startTime}
              onChange={(e) => handleChange("startTime", e.target.value)}
              className={
                errors.startTime
                  ? "border-red-500 focus-visible:ring-red-500"
                  : ""
              }
            />
            {errors.startTime && (
              <p className="text-xs text-red-500">{errors.startTime}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="endTime">
              End Time <span className="text-red-500">*</span>
            </Label>
            <Input
              id="endTime"
              type="time"
              value={form.endTime}
              onChange={(e) => handleChange("endTime", e.target.value)}
              className={
                errors.endTime
                  ? "border-red-500 focus-visible:ring-red-500"
                  : ""
              }
            />
            {errors.endTime && (
              <p className="text-xs text-red-500">{errors.endTime}</p>
            )}
          </div>
        </div>

        {totalHours && (
          <div className="flex items-center gap-2 text-sm text-primary bg-primary/5 border border-primary/10 rounded-lg px-3 py-2 w-fit">
            <Clock className="h-3.5 w-3.5" />
            <span>Total duration: {totalHours} hrs</span>
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Status</Label>
          <div className="flex gap-2">
            {(["active", "inactive"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => handleChange("status", s)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium border transition ${
                  form.status === s
                    ? s === "active"
                      ? "bg-green-50 text-green-600 border-green-600"
                      : "bg-red-50 text-red-600 border-red-600"
                    : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? "Saving..."
              : isEditMode
                ? "Update Shift"
                : "Save Shift"}
          </Button>
        </div>
      </form>
    </CommonModal>
  );
};

export default AddShiftModal;
